import { Fragment, useCallback, useMemo, useState, type CSSProperties } from "react";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import {
	ContextMenu,
	type ContextMenuEntry,
	type ContextMenuPosition,
} from "@/components/common/ContextMenu";
import { StatusDot } from "@/components/common/StatusDot";
import { CloudSharedVolumesTree } from "@/components/file-tree/CloudSharedVolumesTree";
import { FileTreeSkeleton } from "@/components/file-tree/FileTreeSkeleton";
import { SharedVolumePushDialog } from "@/components/file-tree/SharedVolumePushDialog";
import {
	TREE_HOVER_GROUP_CLASS,
	TREE_INDENT_PX,
	TREE_ROOT_PADDING_PX,
} from "@/components/file-tree/TreeIndentGuides";
import {
	DeviceSharedVolumeTreeRow,
	type DeviceSharedVolumeRootPush,
} from "@/components/volumes/DeviceSharedVolumeTreeRow";
import {
	useDeviceSharedVolumeActivity,
	useDeviceSharedVolumes,
	useDeviceSharedVolumeSync,
} from "@/components/volumes/useDeviceSharedVolumes";
import { sharedVolumeEntryMenuItems } from "@/components/volumes/shared-volume-file-actions";
import { SharedVolumeLoadMoreRow } from "@/components/volumes/SharedVolumeLoadMoreRow";
import { useCloudSharedVolumes } from "@/components/volumes/useCloudSharedVolumes";
import { useSharedVolumeDrop } from "@/components/volumes/useSharedVolumeDrop";
import {
	useSharedVolumeTreeExpansion,
	useSharedVolumeTreeSelection,
} from "@/components/volumes/useSharedVolumeTreeNavigation";
import {
	discardDeviceSharedVolumeCopy,
	pushDeviceSharedVolume,
	saveDeviceSharedVolumeRule,
	type DeviceSharedVolume,
	type DeviceSharedVolumePhase,
} from "@/lib/api";
import {
	buildDeviceSharedVolumeRows,
	countUnpushedChanges,
	describeDeviceSharedVolumeActivity,
	describeMovedAside,
	deviceSharedVolumeLocalPath,
	deviceSharedVolumeRowKey,
	findDeviceSharedVolumeEntry,
	groupDeviceSharedVolumeTargets,
	isDeviceSharedVolumeEntryLocal,
	isDeviceSharedVolumeEntryOpenable,
	isDeviceSharedVolumeInTransfer,
	uncountedDeviceSharedVolumesMessage,
	type DeviceSharedVolumeActivityTone,
	type DeviceSharedVolumeEntryRow,
	type DeviceSharedVolumeTarget,
	type UnpushedChangeTally,
} from "@/lib/device-shared-volumes";
import { useHasPermission } from "@/lib/permissions";
import { reportSharedVolumeFailure } from "@/lib/shared-volume-failure";
import { selectCanInsertPathIntoMessage, useChatStore } from "@/stores/useChatStore";
import {
	relistDeviceSharedVolumes,
	useDeviceSharedVolumeStore,
} from "@/stores/useDeviceSharedVolumeStore";
import { useToastStore } from "@/stores/useToastStore";

const ACTIVITY_COLORS: Record<DeviceSharedVolumeActivityTone, string> = {
	warning: "var(--warning)",
	danger: "var(--danger)",
	muted: "var(--fg-3)",
};

/** 行の左のシェブロン・アイコンの枠と、名前との間の幅。 */
const ROW_LEAD_PX = 22;

/** 根の行の下に添える注記。根の直下の行の名前と頭を揃える。 */
const NOTE_STYLE: CSSProperties = {
	margin: 0,
	padding: `2px 12px 2px ${TREE_ROOT_PADDING_PX + TREE_INDENT_PX + ROW_LEAD_PX}px`,
	fontSize: "var(--text-caption)",
	color: "var(--fg-3)",
};

/** 根の行の上に出す様子の行。根の行の名前と頭を揃える。 */
const ACTIVITY_STYLE: CSSProperties = {
	...NOTE_STYLE,
	paddingLeft: TREE_ROOT_PADDING_PX + ROW_LEAD_PX,
	display: "flex",
	alignItems: "center",
	gap: 6,
};

interface PendingDiscard {
	environmentId: string;
	targets: DeviceSharedVolumeTarget[];
	message: string;
}

interface MenuState {
	position: ContextMenuPosition;
	row: DeviceSharedVolumeEntryRow;
	targets: DeviceSharedVolumeTarget[];
	/** 理由は開いたときのものに留める (ポーリングで変わると、ホバー中に title が差し替わってしまう)。 */
	unavailableReason: string | null;
	/** 対象のボリュームのどれかの反映が途中か、頼んだばかりなら、その理由 (開いたときのもの)。 */
	pushInProgressReason: string | null;
	running: boolean;
}

/** 根の行の反映ボタンを押せなくする、反映の途中の段階とその理由。 */
const PUSH_IN_PROGRESS_REASONS: Partial<Record<DeviceSharedVolumePhase, string>> = {
	pushing: "共有ボリュームに反映しています。",
	pushPending: "反映を始めるのを待っています。",
};

/** 反映を頼めない理由 (反映の途中か、頼んだ後のランナーの報告がまだ載っていない)。 */
function pushInProgressReason(
	volume: DeviceSharedVolume,
	pushRequested: ReadonlyMap<string, string | null>,
): string | null {
	if (pushRequested.has(volume.volumeId)) return "共有ボリュームに反映しています。";
	return PUSH_IN_PROGRESS_REASONS[volume.state.phase] ?? null;
}

/** 写しから消えるもの (反映していない変更・対象外) があるか、数えきれていなければ、取り込みをやめる前に確かめる。 */
function needsDiscardConfirm({
	count,
	unsupported,
	partial,
	uncounted,
}: UnpushedChangeTally): boolean {
	return count > 0 || unsupported > 0 || partial || uncounted.length > 0;
}

/** 取り込みをやめる前に確かめる文言。反映していない変更と、反映しない対象外は分けて書く。 */
function discardConfirmMessage({
	count,
	unsupported,
	partial,
	uncounted,
}: UnpushedChangeTally): string {
	const changes: string[] = [];
	if (uncounted.length > 0) changes.push(uncountedDeviceSharedVolumesMessage(uncounted));
	const reason = "一部の場所しか確かめられていません";
	if (partial) {
		changes.push(
			count > 0
				? `反映していない変更が ${count} 件以上あります (${reason})。`
				: `反映していない変更があるかもしれません (${reason})。`,
		);
	} else if (count > 0) {
		changes.push(`反映していない変更が ${count} 件あります。`);
	}
	const unsupportedNote = `対象外 (シンボリックリンクなど) の ${unsupported} 件`;
	if (changes.length === 0) {
		return `取り込みをやめると、手元の写しから${unsupportedNote}も消えます (対象外は共有ボリュームに反映されません)。`;
	}
	const also = unsupported > 0 ? `と、${unsupportedNote}` : "";
	return `${changes.join("")}取り込みをやめると、手元の写しからこの変更${also}も消えます。残すには、先に右クリックの「共有ボリュームに反映」で反映してください。`;
}

/** 操作の後に (失敗しても) 取り直す。取り直しの失敗は別に知らせる。 */
function refreshAfterAction(scan: boolean): void {
	reportSharedVolumeFailure(
		useDeviceSharedVolumeStore.getState().refresh({ scan, relist: "none" }),
		"共有ボリュームの状態を取り直せませんでした。",
	);
}

/** 選んだ行を、取り直した木の今の中身で引き直す。木から消えたものは外し、外した件数を知らせる。 */
function currentTargets(targets: readonly DeviceSharedVolumeTarget[]): DeviceSharedVolumeTarget[] {
	const { directories } = useDeviceSharedVolumeStore.getState();
	const fresh = targets.flatMap((target) => {
		if (target.path === "") return [target];
		const entry = findDeviceSharedVolumeEntry(directories, target.volumeId, target.path);
		return entry ? [{ ...target, entry }] : [];
	});
	const dropped = targets.length - fresh.length;
	if (dropped > 0) {
		useToastStore.getState().show({
			type: "info",
			message: `選んだもののうち ${dropped} 件は見つからなくなったため、対象から外しました。`,
		});
	}
	return fresh;
}

/** ボリュームの根を選んでいて直下をまだ読んでいなければ読む (根の直下から対象外を数えるため)。 */
async function loadTargetRoots(targets: readonly DeviceSharedVolumeTarget[]): Promise<void> {
	const store = useDeviceSharedVolumeStore.getState();
	const unread = new Set(
		targets
			.filter((target) => target.path === "" && !store.directories.has(target.volumeId))
			.map((target) => target.volumeId),
	);
	await Promise.all([...unread].map((volumeId) => store.loadDirectory(volumeId, "")));
}

/**
 * 環境のファイルのツリーの先頭 (~ の上) に、付いている共有ボリュームを 1 つずつ最上位のフォルダとして出す
 * (ボリュームが付いているときだけ。デバイスの環境は写し、クラウドの環境は mount されたボリューム)。
 */
export function SharedVolumesTreeSection() {
	const onDevice = useDeviceSharedVolumes();
	const onCloud = useCloudSharedVolumes();
	if (onDevice) return <SharedVolumesTree />;
	return onCloud ? <CloudSharedVolumesTree /> : null;
}

/**
 * 付けたボリュームを最上位のフォルダにし、クラウドの一覧のうち取り込んでいないものを薄く、取り込んだものの手元の状態を
 * アイコンで出す。選択とキー操作はこの中だけで持つ。右クリックで写しのファイルを開く・パスを使うほか、エージェント管理の
 * 権限があれば取り込む範囲を選び、共有ボリュームに反映する (反映していない変更があるボリュームは根の行のボタンからも)。
 * 同じ権限で、起動中なら PC のファイルや ~ のものを行に落として写しに置ける。
 */
function SharedVolumesTree() {
	const canManageAgents = useHasPermission("manageAgents");
	const canInsertPath = useChatStore(selectCanInsertPathIntoMessage);
	const overview = useDeviceSharedVolumeStore((s) => s.overview);
	const directories = useDeviceSharedVolumeStore((s) => s.directories);
	const loadingDirectories = useDeviceSharedVolumeStore((s) => s.loadingDirectories);
	const refreshing = useDeviceSharedVolumeStore((s) => s.refreshing);
	const pushRequested = useDeviceSharedVolumeStore((s) => s.pushRequested);
	const relisting = useDeviceSharedVolumeStore((s) => s.relisting);
	const awaitedVolumes = useDeviceSharedVolumeStore((s) =>
		s.activityPollsLeft > 0 ? s.activityVolumes : null,
	);
	const transferring = useDeviceSharedVolumeActivity();
	const loadChildren = useCallback((row: DeviceSharedVolumeEntryRow) => {
		const store = useDeviceSharedVolumeStore.getState();
		return store.directories.has(row.key) ? null : store.loadDirectory(row.volumeId, row.path);
	}, []);
	const { expanded, toggleExpanded } = useSharedVolumeTreeExpansion(loadChildren);
	const [menu, setMenu] = useState<MenuState | null>(null);
	const [pendingDiscard, setPendingDiscard] = useState<PendingDiscard | null>(null);
	const [pushTargets, setPushTargets] = useState<DeviceSharedVolumeTarget[] | null>(null);
	/** 返事を待っている操作の対象のボリューム。 */
	const [busyVolumes, setBusyVolumes] = useState<ReadonlySet<string>>(() => new Set());
	const busy = busyVolumes.size > 0;
	const { dropDirectory, dropOsFiles, dropHomeEntries, overwriteDialog } = useSharedVolumeDrop();

	useDeviceSharedVolumeSync();

	const volumes = useMemo(() => overview?.volumes ?? [], [overview]);
	const rows = useMemo(
		() => buildDeviceSharedVolumeRows(volumes, directories, expanded),
		[directories, expanded, volumes],
	);
	const entryRows = useMemo(
		() => rows.filter((row): row is DeviceSharedVolumeEntryRow => row.type === "entry"),
		[rows],
	);
	const {
		selected,
		treeRef,
		currentKey,
		selectRow,
		selectForMenu,
		handleRowKeyDown,
		clearSelectionOnBlank,
	} = useSharedVolumeTreeSelection({ entryRows, toggleExpanded, menuOpen: menu !== null });
	const activity = overview ? describeDeviceSharedVolumeActivity(overview) : [];
	const syncing = refreshing || busy || transferring;
	// 進み具合は文言にせず、そのボリュームの根の行のスピナーで示す (環境全体の数え直しでは出さない)。
	const volumeSyncing = (volume: DeviceSharedVolume) =>
		busyVolumes.has(volume.volumeId) ||
		pushRequested.has(volume.volumeId) ||
		awaitedVolumes?.has(volume.volumeId) === true ||
		relisting.has(volume.volumeId) ||
		volume.listing?.refreshing === true ||
		(overview?.device.online === true && isDeviceSharedVolumeInTransfer(volume));

	const openMenu = useCallback(
		(row: DeviceSharedVolumeEntryRow, event: React.MouseEvent) => {
			event.preventDefault();
			event.stopPropagation();
			const keys = selectForMenu(row);
			const targets = entryRows
				.filter((candidate) => keys.has(candidate.key))
				.map((candidate) => ({
					volumeId: candidate.volumeId,
					path: candidate.path,
					entry: candidate.entry,
				}));
			const targetVolumeIds = new Set(targets.map((target) => target.volumeId));
			setMenu({
				position: { x: event.clientX, y: event.clientY },
				row,
				targets,
				unavailableReason: overview?.device.unavailableReason ?? null,
				pushInProgressReason:
					overview?.volumes
						.filter((volume) => targetVolumeIds.has(volume.volumeId))
						.map((volume) => pushInProgressReason(volume, pushRequested))
						.find((reason) => reason !== null) ?? null,
				running: overview?.running ?? false,
			});
		},
		[entryRows, overview, pushRequested, selectForMenu],
	);

	const runAction = useCallback(
		async (targets: readonly DeviceSharedVolumeTarget[], action: () => Promise<void>) => {
			setBusyVolumes(new Set(targets.map((target) => target.volumeId)));
			try {
				await action();
			} finally {
				setBusyVolumes(new Set());
			}
		},
		[],
	);

	const include = useCallback(
		(targets: DeviceSharedVolumeTarget[]) =>
			runAction(targets, async () => {
				const { environmentId: env } = useDeviceSharedVolumeStore.getState();
				if (env === null) return;
				const notes = new Set<string>();
				const failures = new Set<string>();
				const sent: string[] = [];
				try {
					for (const [volumeId, paths] of groupDeviceSharedVolumeTargets(targets)) {
						for (const path of paths ?? [""]) {
							const sync = await saveDeviceSharedVolumeRule(env, volumeId, path, true);
							if (sync.sent) sent.push(volumeId);
							else if (sync.reason !== null) (sync.failed ? failures : notes).add(sync.reason);
						}
					}
				} finally {
					// 途中で保存に失敗しても、それまでに保存した分の結果を知らせる (失敗はそのまま呼び出し元へ)。
					if (sent.length > 0) useDeviceSharedVolumeStore.getState().expectActivity(sent);
					const toasts = useToastStore.getState();
					if (notes.size > 0) toasts.show({ type: "info", message: [...notes].join(" ") });
					if (failures.size > 0) toasts.show({ type: "error", message: [...failures].join(" ") });
					refreshAfterAction(false);
				}
			}),
		[runAction],
	);

	// 規則の保存と写しの破棄は API が 1 回で行う (消せなければ規則も戻る)。
	const discard = useCallback(async (env: string, targets: DeviceSharedVolumeTarget[]) => {
		try {
			for (const [volumeId, paths] of groupDeviceSharedVolumeTargets(targets)) {
				await discardDeviceSharedVolumeCopy(env, volumeId, paths);
			}
		} finally {
			refreshAfterAction(true);
		}
	}, []);

	// 消す前に手元の変更を数え直し、反映していない変更があれば (数えきれていなければ必ず) 確かめる。
	const requestDiscard = useCallback(
		(targets: DeviceSharedVolumeTarget[]) =>
			runAction(targets, async () => {
				// 待つ間に環境を切り替えたら、別の環境の写しを消さないよう中止する。
				const { environmentId: env, generation } = useDeviceSharedVolumeStore.getState();
				if (env === null) return;
				const stillCurrent = () => useDeviceSharedVolumeStore.getState().generation === generation;
				const scanned = await useDeviceSharedVolumeStore
					.getState()
					.refresh({ scan: true, relist: "none" });
				if (scanned === null) return;
				const fresh = currentTargets(targets);
				if (fresh.length === 0) return;
				await loadTargetRoots(fresh);
				if (!stillCurrent()) return;
				const tally = countUnpushedChanges(
					fresh,
					scanned.volumes,
					useDeviceSharedVolumeStore.getState().directories,
				);
				if (!needsDiscardConfirm(tally)) {
					await discard(env, fresh);
					return;
				}
				setPendingDiscard({
					environmentId: env,
					targets: fresh,
					message: discardConfirmMessage(tally),
				});
			}),
		[discard, runAction],
	);

	const push = useCallback(
		(targets: DeviceSharedVolumeTarget[]) =>
			runAction(targets, async () => {
				const { environmentId: env } = useDeviceSharedVolumeStore.getState();
				if (env === null) return;
				const started: string[] = [];
				try {
					for (const [volumeId, paths] of groupDeviceSharedVolumeTargets(targets)) {
						await pushDeviceSharedVolume(env, volumeId, paths);
						started.push(volumeId);
					}
				} finally {
					// 途中のボリュームで失敗しても、それまでに始めた反映は知らせて進み具合を追う (失敗はそのまま呼び出し元へ)。
					if (started.length > 0) {
						useDeviceSharedVolumeStore.getState().expectPush(started);
						useToastStore
							.getState()
							.show({ type: "info", message: "共有ボリュームへの反映を始めました。" });
					}
				}
			}),
		[runAction],
	);

	const loadMore = useCallback((volumeId: string, path: string) => {
		reportSharedVolumeFailure(
			useDeviceSharedVolumeStore.getState().loadMore(volumeId, path),
			"共有ボリュームの中身を取得できませんでした。",
		);
	}, []);

	const blockDrop = useCallback((event: React.DragEvent) => {
		event.stopPropagation();
		event.preventDefault();
		event.dataTransfer.dropEffect = "none";
	}, []);

	const menuItems = useMemo((): ContextMenuEntry[] => {
		if (menu === null) return [];
		const { row } = menu;
		const single = menu.targets.length === 1;
		const localPath = deviceSharedVolumeLocalPath(row.volume, row.path);
		const items = sharedVolumeEntryMenuItems({
			currentEnvironmentId: () => useDeviceSharedVolumeStore.getState().environmentId,
			volumeId: row.volumeId,
			path: row.path,
			openable: single && isDeviceSharedVolumeEntryOpenable(row.entry),
			running: menu.running,
			pathToCopy: single && isDeviceSharedVolumeEntryLocal(row.entry) ? localPath : null,
			canInsertPath,
		});
		if (canManageAgents) {
			if (items.length > 0) items.push({ separator: true });
			items.push(
				{
					label: "取り込む",
					testId: "shared-volume-menu-include",
					disabled: busy,
					onSelect: () =>
						reportSharedVolumeFailure(
							include(menu.targets),
							"取り込む範囲を保存できませんでした。",
						),
				},
				{
					label: "取り込みをやめる…",
					testId: "shared-volume-menu-exclude",
					disabled: busy || menu.unavailableReason !== null,
					title: menu.unavailableReason ?? undefined,
					onSelect: () =>
						reportSharedVolumeFailure(
							requestDiscard(menu.targets),
							"取り込みをやめられませんでした。",
						),
				},
				{ separator: true },
				{
					label: "共有ボリュームに反映…",
					testId: "shared-volume-menu-push",
					disabled: busy || menu.unavailableReason !== null || menu.pushInProgressReason !== null,
					title: menu.unavailableReason ?? menu.pushInProgressReason ?? undefined,
					onSelect: () => setPushTargets(menu.targets),
				},
			);
		}
		if (row.entry === null) {
			if (items.length > 0) items.push({ separator: true });
			items.push({
				label: "一覧を取り直す",
				testId: "shared-volume-menu-relist",
				disabled: syncing,
				onSelect: relistDeviceSharedVolumes,
			});
		}
		return items;
	}, [busy, canInsertPath, canManageAgents, include, menu, requestDiscard, syncing]);

	const unavailableReason = overview?.device.unavailableReason ?? null;
	const rootPush = (row: DeviceSharedVolumeEntryRow): DeviceSharedVolumeRootPush | null => {
		if (!canManageAgents || row.entry !== null || row.volume.state.unpushedChangeCount === 0) {
			return null;
		}
		const reason = unavailableReason ?? pushInProgressReason(row.volume, pushRequested);
		return {
			disabled: busy || reason !== null,
			reason,
			onPush: () => setPushTargets([{ volumeId: row.volumeId, path: row.path, entry: null }]),
		};
	};

	return (
		<div
			onClick={clearSelectionOnBlank}
			onContextMenu={(event) => {
				event.preventDefault();
				event.stopPropagation();
			}}
			onDragEnter={blockDrop}
			onDragOver={blockDrop}
			onDrop={blockDrop}
			className={TREE_HOVER_GROUP_CLASS}
			data-testid="device-shared-volumes-pane"
		>
			{activity.map((line) => (
				<p
					key={line.id}
					data-testid="shared-volumes-activity"
					data-tooltip={line.detail ?? undefined}
					style={{ ...ACTIVITY_STYLE, color: ACTIVITY_COLORS[line.tone] }}
				>
					<StatusDot tone={line.tone} />
					<span style={{ minWidth: 0 }}>{line.message}</span>
				</p>
			))}
			<div ref={treeRef} data-testid="device-shared-volumes-tree">
				{rows.map((row) => {
					if (row.type === "loadMore") {
						const directoryKey = deviceSharedVolumeRowKey(row.volumeId, row.path);
						return (
							<SharedVolumeLoadMoreRow
								key={row.key}
								directoryKey={directoryKey}
								depth={row.depth}
								loading={loadingDirectories.has(directoryKey)}
								onClick={() => loadMore(row.volumeId, row.path)}
							/>
						);
					}
					const rootLoading = row.entry === null && row.expanded && !directories.has(row.key);
					const rootEmpty =
						row.entry === null && row.expanded && directories.get(row.key)?.entries.length === 0;
					// クラウドの一覧を取り終えるまでは、空でも「無い」とは言えない。
					const listed = row.volume.listing?.complete === true && !row.volume.listing.refreshing;
					const movedAside = row.entry === null ? describeMovedAside(row.volume) : null;
					return (
						<Fragment key={row.key}>
							<DeviceSharedVolumeTreeRow
								row={row}
								selected={selected.has(row.key)}
								current={row.key === currentKey}
								loading={loadingDirectories.has(row.key)}
								syncing={row.entry === null && volumeSyncing(row.volume)}
								push={rootPush(row)}
								canDrop={dropDirectory(row) !== null}
								onDropFiles={dropOsFiles}
								onDropHomeEntries={dropHomeEntries}
								onClick={selectRow}
								onContextMenu={openMenu}
								onKeyDown={handleRowKeyDown}
							/>
							{movedAside && (
								<p
									style={{ ...NOTE_STYLE, overflowWrap: "anywhere" }}
									data-testid={`shared-volume-moved-aside-${row.volumeId}`}
								>
									{movedAside}
								</p>
							)}
							{rootLoading && <FileTreeSkeleton />}
							{rootEmpty && (
								<p style={NOTE_STYLE} data-testid={`shared-volume-empty-${row.volumeId}`}>
									{listed ? "ファイルがありません" : "一覧を取得しています…"}
								</p>
							)}
						</Fragment>
					);
				})}
			</div>
			{menu && menuItems.length > 0 && (
				<ContextMenu
					position={menu.position}
					onClose={() => setMenu(null)}
					testId="shared-volume-menu"
					items={menuItems}
				/>
			)}
			<ConfirmDialog
				open={pendingDiscard !== null}
				title="取り込みをやめる"
				message={pendingDiscard?.message ?? ""}
				confirmLabel="取り込みをやめる"
				onConfirm={async () => {
					if (!pendingDiscard) return;
					await runAction(pendingDiscard.targets, () =>
						discard(pendingDiscard.environmentId, pendingDiscard.targets),
					);
					setPendingDiscard(null);
				}}
				onCancel={() => setPendingDiscard(null)}
			/>
			{overwriteDialog}
			<SharedVolumePushDialog
				targets={pushTargets}
				onPush={push}
				onClose={() => setPushTargets(null)}
			/>
		</div>
	);
}
